import React, { useEffect, useMemo, useState } from 'react';
import { pr as api } from '../services/api';
import { nb, montant, pct1, moisCourt, useFiltresVentes, PanneauVentes, usePanneau, useCanal, SelecteurUnite, basculer } from './ventesUtils';
import { sequentielle, divergente, Echelle } from './heatmap';
import './Ventes.css';
import './VentesJour.css';  // vj-sel : le bandeau « ce qui est filtré »

// ─── PR — Analyse croisée ────────────────────────────────────────────────────
// Matrice dimension × mois, même gabarit que VN/VO et APV.
//
// ⚠ Les CUMULS voyagent, jamais les taux : le taux de marge et le panier moyen
// se recalculent à chaque niveau — cellule, total de ligne, pied. Moyenner des
// pourcentages de mois à volumes différents donne un faux.

const MESURES = [
  { code: 'ca', label: 'CA HT' },
  { code: 'mg', label: 'Marge €' },
  { code: 'taux', label: 'Taux de marge' },
  { code: 'nf', label: 'Factures' },
  { code: 'panier', label: 'Panier moyen' },
];
const TAUX = ['taux', 'panier'];
const MONETAIRES = ['ca', 'mg'];
const DIMS = [
  { code: 'concession', label: 'Concession', pl: 'concessions' },
  { code: 'master', label: 'Concessions groupées', pl: 'regroupements', axe: 'master' },
  { code: 'plaque', label: 'Plaque', pl: 'plaques', axe: 'plaque' },
  { code: 'canal', label: 'Canal', pl: 'canaux' },
  { code: 'marque', label: 'Marque', pl: 'marques' },
  { code: 'atelier', label: 'Atelier', pl: 'ateliers' },
  { code: 'receptionnaire', label: 'Vendeur', pl: 'vendeurs' },
];

const som = (a) => (a || []).reduce((x, y) => x + y, 0);
const cellVal = (o, i, m) => {
  if (m === 'ca') return o.ca[i];
  if (m === 'mg') return o.mg[i];
  if (m === 'nf') return o.nf[i];
  if (m === 'panier') return o.nf[i] ? o.ca[i] / o.nf[i] : null;
  return o.ca[i] ? (o.mg[i] / o.ca[i]) * 100 : null;
};
function totVal(o, m) {
  const ca = som(o.ca), mg = som(o.mg), nf = som(o.nf);
  if (m === 'ca') return ca;
  if (m === 'mg') return mg;
  if (m === 'nf') return nf;
  if (m === 'panier') return nf ? ca / nf : null;
  return ca ? (mg / ca) * 100 : null;
}
const fmt = (v, m, u) => (v == null ? '—'
  : m === 'taux' ? pct1(v)
    : m === 'nf' ? nb(v)
      : m === 'panier' ? montant(v, 'e') : montant(v, u));

// Un taux et un panier se lisent face à leur moyenne pondérée : divergent. Le
// CA, la marge et le nombre de factures n'ont pas de point neutre : séquentiel.
const couleur = (v, m, st) => (TAUX.includes(m) ? divergente(v, st) : sequentielle(v, st.max));
function statsDe(rows, m) {
  const vals = []; let num = 0, den = 0;
  rows.forEach((r) => r.ca.forEach((_, i) => {
    const v = cellVal(r, i, m); if (v != null) vals.push(v);
    if (m === 'panier') { num += r.ca[i]; den += r.nf[i]; }
    else { num += r.mg[i]; den += r.ca[i]; }
  }));
  return {
    min: Math.min(...vals, 0), max: Math.max(...vals, 1),
    mid: TAUX.includes(m) ? (den ? (num / den) * (m === 'panier' ? 1 : 100) : 0) : 0,
  };
}

export default function PrAnalyse({ branche = 'auto-antilles' }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [mesure, setMesure] = useState('ca');
  const [unite, setUnite] = useState('k');
  const [dimension, setDimension] = useState('canal');
  const [ouvert, toggle] = usePanneau();
  const societesDispo = useMemo(() => data?.societesDispo || [], [data]);
  const f = useFiltresVentes(societesDispo);
  const { canal, choix } = useCanal(f);

  useEffect(() => {
    let actif = true; setErreur('');
    api.analyse({ periode: f.periode, mois: f.moisStr, societes: f.societesUrl, dimension, canal, branche })
      .then((r) => { if (actif) setData(r.data); })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
  }, [branche, f.periode, f.moisStr, f.societesUrl, dimension, canal]);

  const Panneau = (
    <PanneauVentes ouvert={ouvert} onToggle={toggle} f={f} societesDispo={societesDispo}
      extra={{ choix }} />
  );
  if (erreur) return <><main className="vt-page"><div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page"><div className="vt-skel" /></main>{Panneau}</>;

  const { mois, rows } = data;
  const st = statsDe(rows, mesure);
  const dimObj = DIMS.find((d) => d.code === dimension) || DIMS[0];

  // Le pied passe par une ligne « ensemble » soumise aux MÊMES fonctions que
  // les cellules : deux chemins de calcul, ce sont deux occasions de diverger.
  const cumul = (ex) => mois.map((_, i) => rows.reduce((t, r) => t + (ex(r)[i] || 0), 0));
  const ensemble = { ca: cumul((r) => r.ca), mg: cumul((r) => r.mg), nf: cumul((r) => r.nf) };
  const totMois = mois.map((_, i) => cellVal(ensemble, i, mesure));
  const totGen = totVal(ensemble, mesure);

  // Concession, regroupement, plaque — et le canal — filtrent la page au clic.
  const filtreCanal = dimension === 'canal';
  const filtrable = dimension === 'concession' || !!dimObj.axe || filtreCanal;
  const cleDe = (o) => (dimObj.axe
    ? (societesDispo || []).filter((x) => x[dimObj.axe] === o.nom).map((x) => x.code).join(',')
    : ((societesDispo || []).find((x) => x.nom === o.nom) || {}).code || '');

  const Cellule = ({ o, i }) => {
    const v = cellVal(o, i, mesure), c = couleur(v, mesure, st);
    return <td style={{ background: c.bg }}><span className={`cell${c.mut ? ' mut' : ''}`} style={c.fg ? { color: c.fg } : undefined}>{fmt(v, mesure, unite)}</span></td>;
  };

  return (
    <>
      <main className="vt-page">
        <div className="vt-head">
          <div><h1>PR — Analyse croisée</h1></div>
          <div className="vt-asof"><b>{data.periode || ''}</b>{canal && <span> · {canal.toLowerCase()}</span>}</div>
        </div>

        {canal && (
          <div className="vj-sel">
            <span>Périmètre limité au <b>{canal.toLowerCase()}</b> — sauf la dimension « Canal », qui est la liste où l'on choisit.</span>
            <button type="button" className="vj-sel-x" onClick={() => f.setFiltre({ canal: '' })}>Voir les deux canaux</button>
          </div>
        )}

        <div className="vt-ctl">
          <span><span className="lab">Mesure</span>
            <span className="vt-seg">{MESURES.map((m) => (
              <button key={m.code} className={mesure === m.code ? 'on' : ''} onClick={() => setMesure(m.code)}>{m.label}</button>
            ))}</span>
          </span>
          <span><span className="lab">Dimension</span>
            <span className="vt-seg">{DIMS.map((d) => {
              const off = d.axe && !(societesDispo || []).some((x) => x[d.axe]);
              return (
                <button key={d.code} disabled={off} className={dimension === d.code ? 'on' : ''}
                  title={off ? `Aucune valeur « ${d.label} » dans le référentiel des sociétés` : ''}
                  onClick={() => setDimension(d.code)}>{d.label}</button>
              );
            })}</span>
          </span>
        </div>

        <div className="vt-card">
          <div className="vt-card-h">
            <h2>{MESURES.find((m) => m.code === mesure).label} — par {dimObj.label.toLowerCase()} et par mois</h2>
            <div className="st-tools">
              {data.tronque && <span className="hint">{nb(rows.length - 1)} premiers sur {nb(data.tronque)} par CA, le reste en « Autres »</span>}
              {MONETAIRES.includes(mesure) && <SelecteurUnite unite={unite} setUnite={setUnite} />}
            </div>
          </div>
          <div className="vt-card-b vt-tbl-wrap">
            <table className="vt-mx tbl-dense">
              <thead><tr><th className="l">{dimObj.label}</th>{mois.map((m) => <th key={m}>{moisCourt(m)}</th>)}
                <th className="tot">{TAUX.includes(mesure) ? 'Moy.' : 'Total'}</th></tr></thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.nom}
                    className={filtrable ? `tbl-clic${(filtreCanal ? canal === o.nom : f.societesUrl === cleDe(o)) ? ' actif' : ''}` : undefined}
                    onClick={!filtrable ? undefined : () => (filtreCanal
                      ? f.setFiltre({ canal: basculer(canal, o.nom) })
                      : f.setFiltre({ societes: basculer(f.societesUrl, cleDe(o)) }))}>
                    <td className="l nodrill">{o.nom}</td>
                    {mois.map((_, i) => <Cellule key={i} o={o} i={i} />)}
                    <td className="tot">{fmt(totVal(o, mesure), mesure, unite)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr><td className="l">Total / moy.</td>
                {totMois.map((v, i) => <td key={i}>{fmt(v, mesure, unite)}</td>)}
                <td className="tot">{fmt(totGen, mesure, unite)}</td></tr></tfoot>
            </table>
            <div className="vt-lgd">
              {TAUX.includes(mesure) ? (
                <><span>Sous la moyenne</span><Echelle divergent /><span>Au-dessus</span>&nbsp;·&nbsp;moyenne groupe : <b>{mesure === 'panier' ? montant(st.mid, 'e') : pct1(st.mid)}</b></>
              ) : (
                <><span>Faible</span><Echelle /><span>Élevé</span></>
              )}
            </div>
          </div>
        </div>
      </main>
      {Panneau}
    </>
  );
}
